import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Heart, X, Save, ArrowRight } from "lucide-react";

const FILTERS = [
  { id: "recent", label: "Recent", daysBack: 30 },
  { id: "month", label: "This month", period: "month" },
  { id: "year", label: "This year", period: "year" },
  { id: "all", label: "All time", daysBack: null },
];

const HAND_FONTS = "var(--font-hand, 'Caveat', 'Kalam', cursive)";

// Deterministic subtle tilt per entry so the wall feels like tape not grid.
function tiltFor(id) {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  const pct = ((h % 1000) / 1000);
  return (pct * 2.4 - 1.2).toFixed(2); // -1.2° … +1.2°
}

// Shadow depth variation
function shadowFor(id) {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i++) h = (h * 17 + s.charCodeAt(i)) & 0xffffffff;
  const depth = 3 + (h % 6); // 3..8px
  return `0 ${depth}px ${depth * 2}px rgba(0,0,0,0.06)`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function firstOfMonthIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function firstOfYearIso() {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
}

export default function GratitudeWall() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("recent");
  const [openEntry, setOpenEntry] = useState(null); // entry object for lightbox
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [remember, setRemember] = useState(null); // "remember this?" featured card

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Fetch a wide window (all-time up to 366) with only_gratitude filter.
        const { data } = await api.get("/checkins?limit=366&only_gratitude=true");
        const items = (data.items || []).filter((c) => (c.gratitude_text || "").trim().length > 0);
        setEntries(items);

        // "Remember this?" — a random entry older than 30 days.
        const cutoff = isoDaysAgo(30);
        const old = items.filter((c) => c.date < cutoff);
        if (old.length > 0) {
          setRemember(old[Math.floor(Math.random() * old.length)]);
        } else {
          setRemember(null);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const visible = useMemo(() => {
    const cfg = FILTERS.find((f) => f.id === filter) || FILTERS[0];
    let since = null;
    if (cfg.daysBack != null) since = isoDaysAgo(cfg.daysBack);
    if (cfg.period === "month") since = firstOfMonthIso();
    if (cfg.period === "year") since = firstOfYearIso();
    if (!since) return entries;
    return entries.filter((c) => c.date >= since);
  }, [entries, filter]);

  const openLightbox = (entry) => {
    setOpenEntry(entry);
    setEditText(entry.gratitude_text || "");
  };
  const closeLightbox = () => {
    setOpenEntry(null);
    setEditText("");
  };

  const saveEdit = async () => {
    if (!openEntry) return;
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/checkins/${openEntry.date}`, { gratitude_text: editText });
      setEntries((arr) => arr.map((e) => (e.date === data.date ? data : e)));
      setOpenEntry(data);
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="pt-2">
        <div className="mb-5 fade-up">
          <span className="eyebrow">softly kept</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>
            Gratitude Wall
          </h1>
          <p className="text-muted mt-1 max-w-xl">
            Everything you've noticed, felt, been warmed by. Come here on the low days.
          </p>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-6 fade-up delay-1" data-testid="gratitude-filters">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className="text-xs px-3 py-1.5 rounded-full transition-colors"
                style={{
                  background: active ? "var(--primary)" : "var(--surface-2)",
                  color: active ? "#fff" : "var(--text)",
                  border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
                }}
                data-testid={`gratitude-filter-${f.id}`}
              >
                {f.label}
              </button>
            );
          })}
          <Link
            to="/journal"
            className="text-xs px-3 py-1.5 rounded-full ml-auto flex items-center gap-1 italic"
            style={{ color: "var(--primary)" }}
            data-testid="gratitude-back-to-journal"
          >
            back to Journal <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>

        {/* Remember this? card */}
        {!loading && remember && (
          <RememberCard entry={remember} onOpen={() => openLightbox(remember)} />
        )}

        {/* Body */}
        {loading ? (
          <div className="text-muted">Loading…</div>
        ) : entries.length === 0 ? (
          <EmptyWall />
        ) : visible.length === 0 ? (
          <EmptyFilter onReset={() => setFilter("all")} />
        ) : (
          <div className="columns-2 lg:columns-3 gap-4 sm:gap-5" data-testid="gratitude-masonry">
            {visible.map((c, idx) => (
              <GratitudeCard
                key={c.id || c.date}
                entry={c}
                onOpen={() => openLightbox(c)}
                delayClass={`fade-up delay-${(idx % 4) + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {openEntry && (
        <Lightbox
          entry={openEntry}
          editText={editText}
          setEditText={setEditText}
          onClose={closeLightbox}
          onSave={saveEdit}
          saving={savingEdit}
        />
      )}
    </AppShell>
  );
}

function GratitudeCard({ entry, onOpen, delayClass }) {
  const text = entry.gratitude_text || "";
  const tilt = tiltFor(entry.id);
  return (
    <button
      onClick={onOpen}
      className={`w-full text-left mb-4 sm:mb-5 break-inside-avoid rounded-2xl px-5 py-4 transition-transform hover:-translate-y-0.5 hover:rotate-0 ${delayClass}`}
      style={{
        background: "color-mix(in oklab, var(--primary-soft) 40%, var(--surface))",
        border: "1px solid var(--border)",
        transform: `rotate(${tilt}deg)`,
        boxShadow: shadowFor(entry.id),
      }}
      data-testid={`gratitude-card-${entry.date}`}
    >
      <div className="text-lg sm:text-xl leading-snug" style={{ color: "var(--text)", fontFamily: HAND_FONTS }}>
        {text}
      </div>
      <div className="mt-3 text-[11px] uppercase tracking-wider text-muted" style={{ fontFamily: "serif" }}>
        {fmtDate(entry.date)}
      </div>
    </button>
  );
}

function RememberCard({ entry, onOpen }) {
  const text = entry.gratitude_text || "";
  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-3xl p-6 sm:p-7 mb-6 fade-up flex flex-col sm:flex-row gap-4 sm:items-center transition-transform hover:-translate-y-0.5"
      style={{
        background: "color-mix(in oklab, var(--accent) 8%, var(--surface))",
        border: "1px dashed var(--border)",
      }}
      data-testid="gratitude-remember-card"
    >
      <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: "color-mix(in oklab, var(--accent) 18%, transparent)", color: "var(--accent)" }}>
        <Heart size={16} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-muted italic mb-1">remember this?</div>
        <div className="text-lg sm:text-xl leading-snug" style={{ color: "var(--text)", fontFamily: HAND_FONTS }}>
          {text}
        </div>
        <div className="text-xs text-muted mt-2" style={{ fontFamily: "serif" }}>{fmtDate(entry.date)}</div>
      </div>
    </button>
  );
}

function EmptyWall() {
  return (
    <div
      className="rounded-3xl px-8 py-12 text-center fade-up"
      style={{
        background: "color-mix(in oklab, var(--primary-soft) 30%, var(--surface))",
        border: "1px dashed var(--border)",
      }}
      data-testid="gratitude-empty"
    >
      <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: "color-mix(in oklab, var(--accent) 18%, transparent)", color: "var(--accent)" }}>
        <Heart size={22} strokeWidth={1.5} />
      </div>
      <h2 className="font-display text-2xl leading-tight mb-2" style={{ color: "var(--text)" }}>
        Your gratitude wall lives here.
      </h2>
      <p className="text-muted max-w-md mx-auto">
        Add your first one on today's check-in. One warm line is enough to start.
      </p>
      <div className="mt-6">
        <Link to="/checkin" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="gratitude-empty-cta">
          Go to check-in <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
}

function EmptyFilter({ onReset }) {
  return (
    <div
      className="rounded-3xl px-8 py-10 text-center fade-up"
      style={{
        background: "var(--surface-2)",
        border: "1px dashed var(--border)",
      }}
      data-testid="gratitude-empty-filter"
    >
      <p className="text-muted italic">
        Nothing here for this window — try{" "}
        <button className="underline" style={{ color: "var(--primary)" }} onClick={onReset} data-testid="gratitude-empty-filter-reset">
          All time
        </button>.
      </p>
    </div>
  );
}

function Lightbox({ entry, editText, setEditText, onClose, onSave, saving }) {
  const dirty = (editText || "") !== (entry.gratitude_text || "");
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "color-mix(in oklab, var(--text) 40%, transparent)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="gratitude-lightbox"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl fade-up"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
        }}
      >
        <div className="p-6 sm:p-7">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Heart size={14} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
              <span className="eyebrow">grateful — {fmtDate(entry.date)}</span>
            </div>
            <button onClick={onClose} className="btn-ghost !p-2" data-testid="gratitude-lightbox-close" aria-label="Close">
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>
          <textarea
            className="input-soft"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={6}
            maxLength={1200}
            data-testid="gratitude-lightbox-input"
            style={{
              minHeight: 160,
              background: "color-mix(in oklab, var(--primary-soft) 22%, var(--surface))",
              fontFamily: HAND_FONTS,
              fontSize: "1.15rem",
              lineHeight: 1.5,
            }}
          />
          <div className="mt-2 text-[11px] text-muted italic">just for you — never shared</div>
          {dirty && (
            <div className="flex justify-end mt-4">
              <button
                onClick={onSave}
                disabled={saving}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
                data-testid="gratitude-lightbox-save"
              >
                <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
